import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Label,
  Input,
  Badge,
  Button,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import { Link } from 'react-router-dom';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import DeleteModal from '../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { api } from '../../config';
import { getAuthToken, isAuthenticated } from '../../auth/tokenvalidation';

const ProcurementList = () => {
  const token = getAuthToken();

  // Authentication validation
  useEffect(() => {
    if (!token) {
      toast.error('Authentication token not found. Please login again.');
      return;
    }
    if (!isAuthenticated()) {
      toast.error('Please login to access this page.');
      return;
    }
  }, [token]);

  // State management for filtering and sorting
  const [data, setData] = useState({ items: [] });
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteModalMulti, setDeleteModalMulti] = useState(false);
  const [invoice, setInvoice] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  // Filter and search states
  const [searchText, setSearchText] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [procTypeFilter, setProcTypeFilter] = useState('all');

  // Pagination and sorting states
  const [totalRows, setTotalRows] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState('procurementNumber');
  const [sortDirection, setSortDirection] = useState('asc');

  // Debounce search text
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(searchText);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  // Handle search input
  const handleSearch = e => {
    setSearchText(e.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle status filter change
  const handleStatusFilter = e => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  // Handle sorting
  const handleSort = (column, direction) => {
    // Map column id to the actual field name used by the API
    const fieldMapping = {
      procurementNumber: 'procurementNumber',
      titleProcurement: 'titleProcurement',
      procurementType: 'procurementType',
      description: 'description',
      statusHps: 'statusHps',
    };

    const sortFieldName =
      fieldMapping[column.id] || column.id || 'procurementNumber';
    setSortField(sortFieldName);
    setSortDirection(direction);
  };

  // Get current sort column ID for DataTable
  const getCurrentSortColumnId = () => {
    // Map API field names back to column IDs
    const fieldToColumnMapping = {
      procurementNumber: 'procurementNumber',
      titleProcurement: 'titleProcurement',
      procurementType: 'procurementType',
      description: 'description',
      statusHps: 'statusHps',
    };
    return fieldToColumnMapping[sortField] || 'procurementNumber';
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchText('');
    setStatusFilter('all');
    setProcTypeFilter('all');
    setCurrentPage(1);
  };

  // useEffect(() => {
  //   setInvoice(procurementTable);
  // }, [procurementTable]);

  const procurementTypeMap = {
    '': '-',
    PelelanganKhusus: 'Pelelangan Khusus',
    PemilihanLangsung: 'Pemilihan Langsung',
    TunjukLangsung: 'Tunjuk Langsung',
  };

  const handleDeleteInvoice = () => {
    if (invoice) {
      // Here you would implement actual delete API call
      // For now, just close modal and show success message
      setDeleteModal(false);
      toast.success('Item deleted successfully');
      // Refresh data after delete
      fetchData(
        currentPage,
        rowsPerPage,
        token,
        debouncedSearchText,
        sortField,
        sortDirection,
        statusFilter,
        procTypeFilter
      );
    }
  };

  const handleDeleteModal = id => {
    const selectedRow = data.items.find(item => item.id === id);
    setInvoice(selectedRow);
    setDeleteModal(true);
  };

  const deleteMultiple = () => {
    // Implement multiple delete functionality
    // This would typically involve deleting selected items
    // For now, just close the modal
    toast.success('Items deleted successfully');
    fetchData(
      currentPage,
      rowsPerPage,
      token,
      debouncedSearchText,
      sortField,
      sortDirection,
      statusFilter,
      procTypeFilter
    ); // Refresh table data after delete
  };

  const columns = [
    {
      id: 'procurementNumber',
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          ID Proc.
        </span>
      ),
      selector: row => row?.procurementNumber || '',
      sortable: true,
      width: '10%',
      cell: row => {
        return (
          <div className='text-truncate' title={row?.procurementNumber || ''}>
            <h6 className='mb-0'>{row.procurementNumber}</h6>
          </div>
        );
      },
    },

    {
      id: 'titleProcurement',
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Title
        </span>
      ),
      selector: row => row?.titleProcurement || '',
      sortable: true,
      width: '10%',
      cell: row => {
        return (
          <div className='text-truncate' title={row?.titleProcurement || ''}>
            <h6 className='mb-0'>{row.titleProcurement}</h6>
          </div>
        );
      },
    },

    {
      id: 'procurementType',
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Type
        </span>
      ),
      selector: row => row?.procurementType || '',
      sortable: false,
      width: '15%',
      cell: row => {
        return (
          <div className='text-truncate'>
            <h6 className='mb-0'>
              {procurementTypeMap[row.procurementType] || '-'}
            </h6>
          </div>
        );
      },
    },

    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Product
        </span>
      ),
      selector: row => row?.productType?.name || '',
      sortable: false,
      width: '15%',
      cell: row => {
        return (
          <div>
            <span className='fw-medium d-block text-truncate'>
              {row?.productType?.name || '-'}
            </span>
            <p className='text-muted mb-0 small text-truncate'>
              {row?.productType?.description || ''}
            </p>
          </div>
        );
      },
    },

    {
      id: 'description',
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Description
        </span>
      ),
      selector: row => row?.description || '',
      sortable: true,
      width: '33%',
      cell: row => {
        return (
          <div className='text-truncate' title={row?.description || ''}>
            <h6 className='mb-0'>{row.description}</h6>
          </div>
        );
      },
    },

    {
      id: 'statusHps',
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Status
        </span>
      ),
      selector: row => row?.statusHps || '',
      sortable: true,
      width: '10%',
      cell: row => {
        return (
          <div className='text-center'>
            <div
              className={`badge fs-12 ${
                row.statusHps === 'Requested' ? 'bg-info' : 'bg-success'
              }`}
            >
              {row.statusHps === 'Requested' ? 'Request' : 'Submitted'}
            </div>
          </div>
        );
      },
    },

    {
      name: (
        <span className='form-label text-uppercase fw-semibold fs-13'>
          Action
        </span>
      ),
      width: '7%',
      sortable: false,
      cell: row => (
        <div className='text-center'>
          <UncontrolledDropdown direction='left'>
            <DropdownToggle
              className='btn btn-soft-secondary btn-sm dropdown'
              tag='button'
            >
              <i className='ri-more-2-fill align-bottom '></i>
            </DropdownToggle>
            <DropdownMenu className='dropdown-menu-end'>
              <DropdownItem tag={Link} to={`../apps-requesthps/upd/${row.id}`}>
                <i className='  ri-align-justify align-bottom me-2 text-info'></i>
                Detail
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      ),
    },
  ];
  document.title = 'List Request HPS ';
  useEffect(() => {
    fetchData(
      currentPage,
      rowsPerPage,
      token,
      debouncedSearchText,
      sortField,
      sortDirection,
      statusFilter,
      procTypeFilter
    );
  }, [
    currentPage,
    rowsPerPage,
    token,
    debouncedSearchText,
    sortField,
    sortDirection,
    statusFilter,
    procTypeFilter,
  ]);

  const fetchData = async (
    currentPage,
    rowsPerPage,
    token,
    search = '',
    sort = '',
    direction = 'asc',
    status = 'all',
    procType = 'all'
  ) => {
    // Validate token before making API call
    if (!token || !isAuthenticated()) {
      toast.error('Authentication required. Please login again.');
      return;
    }

    setIsLoading(true);
    let url = `${api.API_URL}/hps/getall-list-hps?PageNumber=${currentPage}&PageSize=${rowsPerPage}&isProcurementCompleted=true`;
    // Add search parameters
    if (search && search.trim()) {
      url += `&Search=${encodeURIComponent(search.trim())}`;
    }

    // Add status filter
    if (status && status !== 'all') {
      url += `&StatusHps2=${encodeURIComponent(status)}`;
    }

    // Add procurement type filter
    if (procType && procType !== 'all') {
      url += `&ProcurementType=${encodeURIComponent(procType)}`;
    }

    // Add sorting parameters
    if (sort && sort !== '') {
      url += `&SortBy=${sort}`;
      const isDescending = direction === 'desc' ? 'true' : 'false';
      url += `&IsDescending=${isDescending}`;
    }

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        credentials: 'same-origin',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const result = await response.json();

      setData({
        items: result.data.items || [],
      });
      setTotalRows(result.data.totalItems || 0);
    } catch (error) {
      toast.error('Failed to load HPS data');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePageChange = useCallback(page => {
    setCurrentPage(page);
  }, []);

  const handlePerRowsChange = useCallback(newPerPage => {
    setRowsPerPage(newPerPage);
    setCurrentPage(1);
  }, []);

  // Mobile card component for better mobile experience
  const MobileCard = ({ item }) => (
    <div className='card mb-2 d-md-none'>
      <div className='card-body p-3'>
        <div className='d-flex justify-content-between align-items-start mb-2'>
          <h6 className='card-title mb-0 text-truncate flex-grow-1 me-2'>
            {item?.titleProcurement || ''}
          </h6>
          <Badge
            color={item?.statusHps === 'Requested' ? 'info' : 'success'}
            className='flex-shrink-0'
          >
            {item?.statusHps === 'Requested' ? 'Request' : 'Submitted'}
          </Badge>
        </div>

        <div className='mb-2'>
          <small className='text-muted d-block'>
            ID: {item?.procurementNumber || ''}
          </small>
          <small className='text-muted d-block'>
            Type: {procurementTypeMap[item?.procurementType] || '-'}
          </small>
          {item?.productType?.name && (
            <small className='text-muted d-block'>
              Product: {item.productType.name}
            </small>
          )}
        </div>

        {item?.description && (
          <p className='card-text text-muted small mb-2 text-truncate'>
            {item.description}
          </p>
        )}

        <div className='d-flex gap-1'>
          <Link
            to={`../apps-requesthps/upd/${item?.id || ''}`}
            className='btn btn-sm btn-outline-primary flex-fill'
          >
            <i className='ri-edit-line me-1'></i>Edit
          </Link>
          <Button
            color='outline-danger'
            size='sm'
            className='flex-fill'
            onClick={() => handleDeleteModal(item?.id)}
          >
            <i className='ri-delete-bin-line me-1'></i>Delete
          </Button>
        </div>
      </div>
    </div>
  );

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDeleteInvoice()}
          onCloseClick={() => setDeleteModal(false)}
        />
        <DeleteModal
          show={deleteModalMulti}
          onDeleteClick={() => {
            deleteMultiple();
            setDeleteModalMulti(false);
          }}
          onCloseClick={() => setDeleteModalMulti(false)}
        />
        <Container fluid>
          <BreadCrumb title='View Data' pageTitle='Request HPS' />

          <Row>
            <Col lg={12}>
              <Card id='HazardList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center'>
                    <h5 className='card-title mb-0 flex-grow-1'>Request HPS</h5>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  {/* Filter Section */}
                  <Row className='mb-3'>
                    <Col xs={12}>
                      <Row className='g-3'>
                        {/* Search Input */}
                        <Col xs={12} md={6} lg={4}>
                          <Label className='form-label'>Search</Label>
                          <Input
                            type='text'
                            placeholder='Search by title or procurement no...'
                            value={searchText}
                            onChange={handleSearch}
                            className='form-control'
                          />
                        </Col>

                        {/* Status Filter */}
                        <Col xs={12} md={6} lg={3}>
                          <Label className='form-label'>Status</Label>
                          <Input
                            type='select'
                            value={statusFilter}
                            onChange={handleStatusFilter}
                            className='form-control'
                          >
                            <option value='all'>All Status</option>
                            <option value='Requested'>Request</option>
                            <option value='Submitted'>Submitted</option>
                          </Input>
                        </Col>
                        <Col
                          xs={12}
                          md={6}
                          lg={2}
                          className='d-flex align-items-end'
                        >
                          <Button
                            color='secondary'
                            outline
                            onClick={clearFilters}
                            className='w-100'
                          >
                            <i className='ri-refresh-line me-1'></i>
                            <span className='d-none d-sm-inline'>
                              Clear Filters
                            </span>
                            <span className='d-sm-none'>Clear</span>
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                  </Row>

                  {/* Results Info */}
                  <Row className='mb-2'>
                    <Col xs={12}>
                      <div className='d-flex justify-content-between align-items-center flex-wrap'>
                        <small className='text-muted'>
                          Showing {data.items?.length || 0} of {totalRows}{' '}
                          entries
                          {(searchText ||
                            statusFilter !== 'all' ||
                            procTypeFilter !== 'all') &&
                            ' (filtered)'}
                        </small>
                        {searchText && (
                          <div className='d-flex align-items-center'>
                            <span className='badge bg-primary me-2'>
                              <i className='ri-search-line me-1'></i>
                              Search Active
                            </span>
                          </div>
                        )}
                      </div>
                    </Col>
                  </Row>

                  {/* Mobile Card View */}
                  <div className='d-md-none'>
                    {isLoading ? (
                      <div className='table-loading'>
                        <div
                          className='spinner-border text-primary'
                          role='status'
                        >
                          <span className='visually-hidden'>Loading...</span>
                        </div>
                        <p className='mt-2'>Loading HPS data...</p>
                      </div>
                    ) : data.items?.length > 0 ? (
                      data.items.map(item => (
                        <MobileCard key={item.id} item={item} />
                      ))
                    ) : (
                      <div className='table-empty'>
                        <i
                          className='ri-inbox-line'
                          style={{ fontSize: '3rem', opacity: 0.5 }}
                        ></i>
                        <p>No HPS data found</p>
                        {(searchText ||
                          statusFilter !== 'all' ||
                          procTypeFilter !== 'all') && (
                          <small>Try adjusting your filters</small>
                        )}
                      </div>
                    )}

                    {/* Mobile Pagination Controls */}
                    <div className='d-flex flex-column align-items-center mt-3 px-2 py-2 border-top'>
                      <div className='d-flex align-items-center mb-2'>
                        <span className='text-muted me-2 small'>
                          Rows per page:
                        </span>
                        <select
                          className='form-select form-select-sm'
                          style={{ width: 'auto' }}
                          value={rowsPerPage}
                          onChange={e =>
                            handlePerRowsChange(parseInt(e.target.value))
                          }
                        >
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>

                      <div className='d-flex flex-column align-items-center'>
                        <span className='text-muted mb-2 small'>
                          {totalRows > 0 ? (
                            <>
                              {(currentPage - 1) * rowsPerPage + 1}-
                              {Math.min(currentPage * rowsPerPage, totalRows)}{' '}
                              of {totalRows}
                            </>
                          ) : (
                            '0 of 0'
                          )}
                        </span>

                        <div className='btn-group' role='group'>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={currentPage === 1 || isLoading}
                            onClick={() => handlePageChange(1)}
                          >
                            <i className='ri-skip-back-line'></i>
                          </button>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={currentPage === 1 || isLoading}
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            <i className='ri-arrow-left-s-line'></i>
                          </button>
                          <span className='btn btn-outline-secondary btn-sm disabled'>
                            {currentPage}
                          </span>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={
                              currentPage >=
                                Math.ceil(totalRows / rowsPerPage) || isLoading
                            }
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            <i className='ri-arrow-right-s-line'></i>
                          </button>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={
                              currentPage >=
                                Math.ceil(totalRows / rowsPerPage) || isLoading
                            }
                            onClick={() =>
                              handlePageChange(
                                Math.ceil(totalRows / rowsPerPage)
                              )
                            }
                          >
                            <i className='ri-skip-forward-line'></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Desktop Table View */}
                  <div className='d-none d-md-block'>
                    <div className='table-responsive'>
                      {isLoading ? (
                        <div className='table-loading text-center py-4'>
                          <div
                            className='spinner-border text-primary'
                            role='status'
                          >
                            <span className='visually-hidden'>Loading...</span>
                          </div>
                          <p className='mt-2'>Loading HPS data...</p>
                        </div>
                      ) : (
                        <DataTable
                          key={`sort-${getCurrentSortColumnId()}-${sortDirection}`}
                          columns={columns}
                          data={data.items || []}
                          pagination={false}
                          onSort={handleSort}
                          sortServer
                          defaultSortFieldId={getCurrentSortColumnId()}
                          defaultSortAsc={sortDirection === 'asc'}
                          highlightOnHover
                          responsive
                          striped
                          persistTableHead
                          progressPending={isLoading}
                          progressComponent={
                            <div className='text-center py-4'>
                              <div
                                className='spinner-border text-primary'
                                role='status'
                              >
                                <span className='sr-only'>Loading...</span>
                              </div>
                              <p className='mt-2 text-muted'>Loading data...</p>
                            </div>
                          }
                          dense
                          customStyles={{
                            header: {
                              style: {
                                backgroundColor: '#f0f0f0',
                                color: '#333',
                                fontWeight: 'bold',
                                minHeight: '40px',
                              },
                            },
                            headCells: {
                              style: {
                                backgroundColor: '#f0f0f0',
                                fontWeight: 'bold',
                                fontSize: '14px',
                                paddingLeft: '12px',
                                paddingRight: '12px',
                                '&:hover': {
                                  backgroundColor: '#e9ecef',
                                },
                              },
                            },
                            cells: {
                              style: {
                                paddingLeft: '12px',
                                paddingRight: '12px',
                                paddingTop: '4px',
                                paddingBottom: '4px',
                                fontSize: '14px',
                              },
                            },
                            rows: {
                              style: {
                                minHeight: '50px',
                                '&:hover': {
                                  backgroundColor: '#f8f9fa',
                                  cursor: 'pointer',
                                },
                              },
                            },
                            table: {
                              style: {
                                minWidth: '100%',
                                tableLayout: 'fixed',
                              },
                            },
                            tableWrapper: {
                              style: {
                                display: 'block',
                                maxWidth: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                              },
                            },
                          }}
                          noDataComponent={
                            <div className='table-empty text-center py-5'>
                              <i
                                className='ri-inbox-line text-muted'
                                style={{ fontSize: '3rem', opacity: 0.5 }}
                              ></i>
                              <p className='mt-2 mb-0'>No HPS data found</p>
                              {(searchText ||
                                statusFilter !== 'all' ||
                                procTypeFilter !== 'all') && (
                                <small className='text-muted'>
                                  Try adjusting your filters
                                </small>
                              )}
                            </div>
                          }
                        />
                      )}
                    </div>

                    {/* Custom Pagination Controls - Outside Scrollable Area */}
                    <div className='d-flex justify-content-between align-items-center mt-3 px-3 py-2 border-top'>
                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-2'>Rows per page:</span>
                        <select
                          className='form-select form-select-sm'
                          style={{ width: 'auto' }}
                          value={rowsPerPage}
                          onChange={e =>
                            handlePerRowsChange(parseInt(e.target.value))
                          }
                        >
                          <option value={10}>10</option>
                          <option value={15}>15</option>
                          <option value={25}>25</option>
                          <option value={50}>50</option>
                        </select>
                      </div>

                      <div className='d-flex align-items-center'>
                        <span className='text-muted me-3'>
                          {totalRows > 0 ? (
                            <>
                              {(currentPage - 1) * rowsPerPage + 1}-
                              {Math.min(currentPage * rowsPerPage, totalRows)}{' '}
                              of {totalRows}
                            </>
                          ) : (
                            '0 of 0'
                          )}
                        </span>

                        <div className='btn-group' role='group'>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={currentPage === 1 || isLoading}
                            onClick={() => handlePageChange(1)}
                          >
                            <i className='ri-skip-back-line'></i>
                          </button>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={currentPage === 1 || isLoading}
                            onClick={() => handlePageChange(currentPage - 1)}
                          >
                            <i className='ri-arrow-left-s-line'></i>
                          </button>
                          <span className='btn btn-outline-secondary btn-sm disabled'>
                            {currentPage}
                          </span>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={
                              currentPage >=
                                Math.ceil(totalRows / rowsPerPage) || isLoading
                            }
                            onClick={() => handlePageChange(currentPage + 1)}
                          >
                            <i className='ri-arrow-right-s-line'></i>
                          </button>
                          <button
                            type='button'
                            className='btn btn-outline-secondary btn-sm'
                            disabled={
                              currentPage >=
                                Math.ceil(totalRows / rowsPerPage) || isLoading
                            }
                            onClick={() =>
                              handlePageChange(
                                Math.ceil(totalRows / rowsPerPage)
                              )
                            }
                          >
                            <i className='ri-skip-forward-line'></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ProcurementList;
